import React from 'react';
import type { DateCellProps } from './types';

/**
 * DateCell - Renders date/timestamp values
 * 
 * Supports three formats:
 * - full: "2024-01-15 14:30:00 UTC" (ISO with time, for software/data systems)
 * - long: "January 15, 2024" (human readable)
 * - short: "01-15-2024" (numeric date)
 */

// Format date as "January 15, 2024"
const formatLong = (date: Date): string => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  
  return `${month} ${day}, ${year}`;
};

// Format date as "01-15-2024"
const formatShort = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${month}-${day}-${year}`;
};

// Format date as "2024-01-15 14:30:00 UTC"
const formatFull = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
};

export const DateCell: React.FC<DateCellProps> = ({ value, config, row }) => {
  // Handle null/undefined/empty values
  if (value === null || value === undefined || value === '') {
    return <span className="table-cell-text">—</span>;
  }

  // Parse the date - handle various input formats
  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string' || typeof value === 'number') {
    date = new Date(value);
  } else {
    return <span className="table-cell-text">Invalid Date</span>;
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return <span className="table-cell-text">Invalid Date</span>;
  }

  // Apply timezone conversion if specified
  if (config?.timezone && config.timezone !== 'UTC') {
    try {
      const timeZoneDate = new Date(date.toLocaleString('en-US', { timeZone: config.timezone }));
      date = timeZoneDate;
    } catch (error) {
      // Invalid timezone, use original date
    }
  }

  // Format based on configuration
  let displayValue: string;

  if (config?.customFormat) {
    displayValue = config.customFormat(date, value, row);
  } else {
    const format = config?.format || 'full';
    
    switch (format) {
      case 'long':
        displayValue = formatLong(date);
        break;
      case 'short':
        displayValue = formatShort(date);
        break;
      case 'full':
      default:
        displayValue = formatFull(date);
        break;
    }
  }

  // Determine text color and weight (matching TextCell)
  const colorMode = config?.color || 'default';
  const textColor = colorMode === 'strong' 
    ? 'var(--core-text-primary)'   // strong = primary (darker)
    : 'var(--core-text-secondary)'; // default = secondary (lighter)
  const fontWeight = config?.fontWeight || 400;  // Default: 400 (normal)

  return (
    <span 
      className={`table-cell-text ${config?.className || ''}`}
      title={displayValue}
      style={{
        fontSize: '0.875rem',
        color: textColor,
        fontWeight
      }}
    >
      {displayValue}
    </span>
  );
};

